import React, { FC, useEffect, useState } from "react";
import {
  useParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import classNames from "classnames";

import { ExercisesTop, Menu, Preloader, TopNav, Workouts } from "@/components";
import {
  useCreateExercizeMutation,
  useCreateTrainingMutation,
  useGetExercizesMutation,
  useRemoveExercizeFromTrainingMutation,
} from "@/store/trainingsApi";
import { AddExerciseModal } from "@/features/add-exercise-modal";
import { EditExerciseModal } from "@/features/edit-exercise-modal";

import { formatScheduleDate } from "@/shared/utils/";
import { hasActiveSubscription } from "@/shared/utils/subscriptionUtils";
import { tg } from "@/constants/telegram";
import { User } from "@/entities/user";
import toast from "react-hot-toast";
import { Training } from "@/components/Trains/model/types";

type TrainPageProps = {
  initData: string;
  user: User;
};

export const TrainPage: FC<TrainPageProps> = (props) => {
  const { initData, user } = props;
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const [training, setTraining] = useState<Training | null>(null);
  const [exercizes, setExercizes] = useState<any[] | null>(null);
  const [isDevelop, setIsDevelop] = useState(false);
  const [trainingIndex, setTrainingIndex] = useState<number>(-1);
  const [trainingCount, setTrainingCount] = useState<number>(0);
  const [allowedTrainingIds, setAllowedTrainingIds] = useState<number[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showAddExerciseModal, setShowAddExerciseModal] = useState(false);
  const [showEditExerciseModal, setShowEditExerciseModal] = useState(false);
  const [editingExerciseId, setEditingExerciseId] = useState<number | null>(null);
  const [editingExercise, setEditingExercise] = useState<any | null>(null);

  const [createExercisez] = useCreateExercizeMutation();
  const [createTraining] = useCreateTrainingMutation();
  const [getExercizes] = useGetExercizesMutation();
  const [removeExercize] = useRemoveExercizeFromTrainingMutation();

  // Проверяем есть ли у пользователя активная подписка
  const hasActiveSubscriptionStatus = hasActiveSubscription(user);

  const isExercisePage =
    location.pathname === `/train-page/${id}/exercise-page`;
  const linkPath = isExercisePage ? `/train-page/0` : `/trains`;

  useEffect(() => {
    tg.ready();
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        const [trainingResponse] = await Promise.all([
          createTraining().unwrap(),
        ]);

        // Новая структура: trainings это объект с created_by_user и schedule
        const trainingsData = trainingResponse.trainings || {};
        const allTrainings = [
          ...(Array.isArray(trainingsData.created_by_user) ? trainingsData.created_by_user : []),
          ...(Array.isArray(trainingsData.schedule) ? trainingsData.schedule : [])
        ];
        const count = trainingResponse.training_count || 0;
        
        setTrainingCount(count);
        
        const parsedId = parseInt(id);
        const selectedTraining = allTrainings.find(
          (item: Training) => item.id === parsedId
        );

        if (!selectedTraining) {
          throw new Error("Training not found");
        }

        // Определяем тип тренировки: если она в created_by_user, то это тренировка пользователя
        const isUserTraining = trainingsData.created_by_user?.some(
          (train: Training) => train.id === parsedId
        ) || false;
        
        // Для всех тренировок (включая свои) используем training_type: "schedule"
        const exercisesResponse = await getExercizes({
          training_id: Number(id),
          training_type: "schedule" as "free" | "schedule",
        }).unwrap();

        setTraining(selectedTraining);
        const loadedExercises = Array.isArray(exercisesResponse.exercizes) 
          ? exercisesResponse.exercizes 
          : [];
        setExercizes(loadedExercises);
        
        // Проверяем режим редактирования из URL и включаем его
        const editModeParam = searchParams.get("editMode") === "true";
        if (editModeParam) {
          setIsEditMode(true);
        }
        
        // Find the index of this training in the list
        const trainingIndexInList = allTrainings.findIndex(
          (item: Training) => item.id === parsedId
        );
        setTrainingIndex(trainingIndexInList);

        const accessibleIds = (() => {
          if (!allTrainings || allTrainings.length === 0) {
            return [];
          }

          // Если есть подписка, все тренировки доступны
          if (hasActiveSubscriptionStatus) {
            return allTrainings.map((trainItem: Training) => trainItem.id);
          }

          // Если нет подписки, доступны только первые training_count актуальных тренировок
          // НО должно быть открыто НЕ МЕНЕЕ 3 тренировок
          // Фильтруем только актуальные тренировки (status === 'wait' || null)
          const currentTrainings = allTrainings.filter(
            (train: Training) => train.status === 'wait' || train.status === null || train.status === undefined
          );

          // Сортируем актуальные тренировки по дате
          const sortedTrainings = [...currentTrainings].sort((a, b) => {
            const firstDate = new Date(a.schedule).getTime();
            const secondDate = new Date(b.schedule).getTime();
            return firstDate - secondDate;
          });

          // Используем максимум из training_count и 3 (минимум 3 тренировки)
          const allowedCount = Math.max(3, count);
          const allowedTrainings = sortedTrainings.slice(0, allowedCount);

          return allowedTrainings.map((trainItem: Training) => trainItem.id);
        })();

        setAllowedTrainingIds(accessibleIds);

        if (exercisesResponse.environment !== "production") {
          setIsDevelop(true);
        }

        const openFirstTrain = searchParams.get("openFirstTrain");
        if (openFirstTrain === "true" && loadedExercises.length > 0) {
          const firstExerciseId = loadedExercises[0].id;
          const trainingId = selectedTraining.id;
          const tgLink = `https://t.me/FitGuid_bot?start=exercize-${trainingId}-${firstExerciseId}`;
          window.location.href = tgLink;
          tg.close();
        }
      } catch (err) {
        console.error("Error:", err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, getExercizes, createTraining, searchParams, navigate]);

  // Автоматически открываем модальное окно для пустых тренировок в режиме редактирования
  // Или если тренировка пустая и пользователь только что создал её
  useEffect(() => {
    if (!isLoading && exercizes !== null && Array.isArray(exercizes) && exercizes.length === 0 && training) {
      const editModeParam = searchParams.get("editMode") === "true";
      // Определяем, является ли тренировка созданной пользователем по sample_type
      const isUserTraining = training.sample_type?.startsWith("createby") || false;
      
      // Если editMode=true или это пользовательская тренировка, показываем кнопку/модальное окно
      if (editModeParam) {
        setIsEditMode(true);
        // Небольшая задержка для корректного отображения после загрузки
        const timer = setTimeout(() => {
          setShowAddExerciseModal(true);
        }, 500);
        return () => clearTimeout(timer);
      } else if (isUserTraining) {
        // Для пустых пользовательских тренировок автоматически включаем режим редактирования
        setIsEditMode(true);
      }
    }
  }, [isLoading, exercizes, searchParams, training]);

  if (isLoading) {
    return (
      <>
        <Preloader hasMenu />
        <Menu active={0} />
      </>
    );
  }

  const handleRefreshExercises = async () => {
    if (!id) return;
    
    try {
      // Для всех тренировок (включая свои) используем training_type: "schedule"
      const exercisesResponse = await getExercizes({
        training_id: Number(id),
        training_type: "schedule" as "free" | "schedule",
      }).unwrap();
      setExercizes(exercisesResponse.exercizes || []);
    } catch (error) {
      console.error("Ошибка обновления упражнений:", error);
    }
  };

  const handleRemoveExercise = async (exerciseId: number) => {
    if (!id) return;
    
    try {
      await removeExercize({
        training_id: Number(id),
        exercize_id: exerciseId,
      }).unwrap();
      toast.success("Упражнение удалено");
      await handleRefreshExercises();
    } catch (error: any) {
      console.error("Ошибка удаления упражнения:", error);
      toast.error(error?.data?.detail || "Ошибка при удалении упражнения");
    }
  };

  const handleEditExercise = (exerciseId: number) => {
    const exercise = exercizes?.find((ex) => ex.id === exerciseId);
    if (exercise) {
      setEditingExercise(exercise);
      setEditingExerciseId(exerciseId);
      setShowEditExerciseModal(true);
    }
  };

  if (!training || exercizes === null) {
    return (
      <div>
        <Menu active={0} />
      </div>
    );
  }

  // Определяем тип тренировки по sample_type
  const isUserTraining = training?.sample_type?.startsWith("createby") || false;
  const trainingType = isUserTraining ? "free" : "schedule";
  const isEmptyTraining = exercizes.length === 0;
  // Режим редактирования доступен для всех типов тренировок, если включен editMode
  const showEditControls = isEditMode;

  return (
    <div className="train-page">
      <div className="container" style={{ position: "relative" }}>
        <TopNav
          linkPath={linkPath}
          handleSetActivePage={() => null}
          title={formatScheduleDate(training.schedule)}
        />
        {training.title_photo && training.title_photo !== "none" && (
          <div className="train-page__picture">
            <img src={training.title_photo} alt="" />
          </div>
        )}
        <div
          className={classNames("exercises", {
            "no-image": training.title_photo === "none",
          })}
        >
          <ExercisesTop
            title={training.title}
            isExercisePage={isExercisePage}
            duration={training.duration || 0}
            id={Number(id)}
          />
          
          {/* Кнопки управления для режима редактирования */}
          {showEditControls && (
            <div style={{ marginBottom: "20px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button
                onClick={() => setShowAddExerciseModal(true)}
                style={{
                  padding: "12px 24px",
                  backgroundColor: "#01bb16",
                  color: "#fff",
                  border: "none",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                + Добавить упражнение
              </button>
              <button
                onClick={() => {
                  setIsEditMode(false);
                  navigate(`/train-page/${id}`);
                }}
                style={{
                  padding: "12px 24px",
                  backgroundColor: "#666",
                  color: "#fff",
                  border: "none",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Завершить редактирование
              </button>
            </div>
          )}

          {/* Для пустых тренировок сразу показываем кнопку добавления упражнения */}
          {isEmptyTraining && !showEditControls && (
            <div style={{ textAlign: "center", padding: "40px 20px" }}>
              <p style={{ color: "#666", fontSize: "16px", marginBottom: "20px" }}>
                Тренировка пустая. Добавьте упражнения для начала тренировки.
              </p>
              <button
                onClick={() => {
                  setIsEditMode(true);
                  setShowAddExerciseModal(true);
                }}
                style={{
                  padding: "12px 24px",
                  backgroundColor: "#01bb16",
                  color: "#fff",
                  border: "none",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                + Добавить упражнение
              </button>
            </div>
          )}

          {/* Если тренировка пустая и editMode=true, автоматически показываем кнопку */}
          {isEmptyTraining && showEditControls && (
            <div style={{ textAlign: "center", padding: "40px 20px" }}>
              <button
                onClick={() => setShowAddExerciseModal(true)}
                style={{
                  padding: "12px 24px",
                  backgroundColor: "#01bb16",
                  color: "#fff",
                  border: "none",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                + Добавить упражнение
              </button>
            </div>
          )}

          {!isEmptyTraining && !showEditControls && isUserTraining && (
            <div style={{ marginBottom: "20px" }}>
              <button
                onClick={() => {
                  setIsEditMode(true);
                  navigate(`/train-page/${id}?editMode=true`);
                }}
                style={{
                  padding: "12px 24px",
                  backgroundColor: "#667eea",
                  color: "#fff",
                  border: "none",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Режим редактирования
              </button>
            </div>
          )}

          {!isEmptyTraining && (
            <Workouts
              isDevelop={isDevelop}
              oldDate={training.schedule}
              trainingId={training.id}
              exercizes={exercizes}
              hasActiveSubscription={hasActiveSubscriptionStatus}
              trainingIndex={trainingIndex}
              trainingCount={trainingCount}
              allowedTrainingIds={allowedTrainingIds}
              isEditMode={showEditControls}
              onRemoveExercise={showEditControls ? handleRemoveExercise : undefined}
              onEditExercise={showEditControls ? handleEditExercise : undefined}
              isFree={trainingType === "free"}
            />
          )}
        </div>
      </div>

      {showAddExerciseModal && (
        <AddExerciseModal
          onClose={() => setShowAddExerciseModal(false)}
          initData={initData}
          trainingId={Number(id)}
          onSuccess={handleRefreshExercises}
        />
      )}

      {showEditExerciseModal && editingExercise && editingExerciseId && (
        <EditExerciseModal
          onClose={() => {
            setShowEditExerciseModal(false);
            setEditingExercise(null);
            setEditingExerciseId(null);
          }}
          initData={initData}
          trainingId={Number(id)}
          exerciseId={editingExerciseId}
          currentExercise={{
            id: editingExercise.id,
            title: editingExercise.title,
            title_photo: editingExercise.title_photo,
            muscle_group: editingExercise.muscle_group,
          }}
          currentApproaches={
            editingExercise.config && editingExercise.config.length > 0
              ? editingExercise.config.map((cfg: any) => ({
                  approach_number: cfg.approach_num || 1,
                  repetitions: cfg.repetitions || 10,
                  weight: cfg.weight?.toString() || "",
                  repetitions_margin: cfg.repetition_margin || undefined,
                }))
              : [
                  {
                    approach_number: 1,
                    repetitions: editingExercise.repetitions || 10,
                    weight: editingExercise.weight?.toString() || "",
                  },
                ]
          }
          onSuccess={handleRefreshExercises}
        />
      )}
    </div>
  );
};

